// The portable workflow validator: paste or open a workflow file, see what OCS would refuse in
// it, and watch it run on the canvas when it holds together. Nothing is uploaded.
import { CheckCircle, FileArrowUp, WarningCircle } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { useTitle } from "../../ui/hooks";
import { PageHero } from "../../ui/Section";
import { parseDocument, validateDocument } from "../../workflows/validate";
import { WorkflowPlayer } from "../../workflows/LazyPlayer";

export function Validator() {
  useTitle("Workflow validator");
  const [text, setText] = useState("");
  const result = useMemo(() => {
    if (!text.trim()) return undefined;
    const { doc, issues } = parseDocument(text);
    return doc ? { doc, issues: validateDocument(doc) } : { doc, issues };
  }, [text]);

  const open = async (file: File | undefined) => {
    if (file) setText(await file.text());
  };

  return (
    <>
      <PageHero kicker="§ Free tools · Validator" title={<>Check a workflow before you trust it.</>}>
        Paste a <Link to="/portable-workflows" className="link">portable workflow</Link> or open one you downloaded. The checks run in your browser; the file goes nowhere.
      </PageHero>
      <section className="wrap grid gap-8 py-12 lg:grid-cols-[1fr_1fr]">
        <div>
          <div className="flex items-center justify-between gap-3">
            <label htmlFor="validator-text" className="text-[14px] font-semibold">
              Workflow JSON
            </label>
            <label className="btn-line cursor-pointer !px-3.5 !py-1.5 text-[13px]">
              <FileArrowUp weight="bold" className="h-4 w-4" aria-hidden /> Open a file
              <input type="file" accept=".json,application/json" className="sr-only" onChange={(e) => void open(e.target.files?.[0])} />
            </label>
          </div>
          <textarea id="validator-text" value={text} onChange={(e) => setText(e.target.value)} rows={18} spellCheck={false} placeholder='{"format": "ocs-workflow-export/1", …}' className="field mt-2 font-mono text-[12.5px]" />
        </div>
        <div aria-live="polite">
          {result === undefined ? (
            <div className="card p-6 text-ink2">The result appears here. Need a file? Download any version from the <Link to="/workflows" className="link">marketplace</Link>.</div>
          ) : result.issues.length === 0 ? (
            <div className="card border-moss/50 p-6">
              <p className="flex items-center gap-2 font-display text-2xl font-bold text-moss">
                <CheckCircle weight="fill" className="h-7 w-7" aria-hidden /> Holds together
              </p>
              <p className="mt-2 text-ink2">
                {result.doc!.workflow.name}: {result.doc!.version.definition.nodes.length} steps, {result.doc!.version.definition.edges.length} branches. OCS applies the same graph rules on publish, and has the final word.
              </p>
            </div>
          ) : (
            <div className="card border-signal/50 p-6">
              <p className="flex items-center gap-2 font-display text-2xl font-bold text-signal">
                <WarningCircle weight="fill" className="h-7 w-7" aria-hidden /> {result.issues.length} {result.issues.length === 1 ? "issue" : "issues"}
              </p>
              <ul className="mt-4 space-y-2">
                {result.issues.map((i, n) => (
                  <li key={n} className="text-[14.5px]">
                    <span className="font-mono text-[12.5px] font-semibold text-ink">{i.where}</span> <span className="text-ink2">{i.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
      {result?.doc && result.issues.length === 0 ? (
        <section className="wrap">
          <div className="card h-[520px] overflow-hidden">
            <WorkflowPlayer graph={result.doc.version.definition} className="h-full" />
          </div>
        </section>
      ) : null}
    </>
  );
}
